import { useState } from 'react';
import { getSafeRedirectUrl } from './getSafeRedirectUrl';

const API_BASE = process.env.GQL_URI ?? '';

export interface ConsentParams {
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  scope: string;
}

export function useMcpConsent({
  clientId,
  redirectUri,
  state,
  codeChallenge,
  codeChallengeMethod,
  scope,
}: ConsentParams) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/mcp/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          state,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          ...(scope && { scope }),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(
          body.error_description ??
            body.error ??
            'Something went wrong. Please contact an admin if the issue persists.',
        );
        setLoading(false);
        return;
      }

      const { redirectUrl } = await res.json();
      window.location.href = getSafeRedirectUrl(redirectUrl);
    } catch {
      setError('Something went wrong. Please contact an admin if the issue persists.');
      setLoading(false);
    }
  };

  const handleDeny = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/mcp/deny`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ client_id: clientId, redirect_uri: redirectUri, state }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(
          body.error_description ??
            body.error ??
            'Something went wrong. Please contact an admin if the issue persists.',
        );
        return;
      }

      window.location.href = getSafeRedirectUrl(body.redirectUrl);
    } catch {
      setError('Something went wrong. Please contact an admin if the issue persists.');
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, handleApprove, handleDeny };
}
